import { createFileRoute, Link } from "@tanstack/react-router";
import { Code } from "../../components/Code";

export const Route = createFileRoute("/docs/nextjs")({
  component: NextjsGuide,
});

// Single source per snippet — passed to both the rendered block and its copy button.
const ENV = `# publishable key — safe in the browser
NEXT_PUBLIC_NOTIFY_KEY=ntfy_pk_your_publishable_key
# secret key — server-side only, never NEXT_PUBLIC_
NOTIFY_SECRET=ntfy_sk_your_secret_key`;

const SW = `importScripts("https://api.getnotify.dev/sw.js");`;

const LAYOUT = `import Script from "next/script";

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <body>
        {children}
        <Script
          src={"https://api.getnotify.dev/notify.js?token=" + process.env.NEXT_PUBLIC_NOTIFY_KEY}
          strategy="afterInteractive"
        />
      </body>
    </html>
  );
}`;

const BUTTON = `"use client";

declare global {
  interface Window {
    notify?: { subscribe: (userId: string) => Promise<unknown> };
  }
}

export function EnableNotifications({ userId }: { userId: string }) {
  return (
    <button onClick={() => window.notify?.subscribe(userId).catch(console.error)}>
      Enable notifications
    </button>
  );
}`;

const ACTION = `"use server";

export async function notifyUser(userId: string, body: string) {
  const res = await fetch("https://api.getnotify.dev/send", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      token: process.env.NOTIFY_SECRET,
      userId,
      title: "Acme",
      body,
    }),
  });
  if (!res.ok) throw new Error("notify: send failed " + res.status);
}`;

const CALL = `// e.g. when a background job finishes
await notifyUser(user.id, "Your export is ready!");`;

function NextjsGuide() {
  return (
    <>
      <h1>Next.js</h1>
      <p className="lead">
        Add native browser push to a Next.js App Router app. Four small pieces — a service worker
        file, the script, a subscribe button, and a server action to send.
      </p>

      <h2>Prerequisites</h2>
      <p>
        Get your keys with <span className="icode">npx notify-dev init</span>, then add them to{" "}
        <span className="icode">.env.local</span>:
      </p>
      <Code fname=".env.local" copyText={ENV}>
        {ENV}
      </Code>
      <div className="callout">
        Two keys: the <strong>publishable</strong> key goes in the browser (script tag); the{" "}
        <strong>secret</strong> key stays on your server and is the only one that can send. Never put
        the secret key in a <span className="icode">NEXT_PUBLIC_</span> variable.
      </div>

      <h2>1. Add the service worker</h2>
      <p>
        Create <span className="icode">public/notify-sw.js</span> (Next serves it at{" "}
        <span className="icode">/notify-sw.js</span>, same-origin with your pages):
      </p>
      <Code fname="public/notify-sw.js" copyText={SW}>
        {SW}
      </Code>

      <h2>2. Load notify.js</h2>
      <p>
        Load the script once in your root layout with <span className="icode">next/script</span>:
      </p>
      <Code fname="app/layout.tsx" copyText={LAYOUT}>
        {LAYOUT}
      </Code>

      <h2>3. Subscribe a user</h2>
      <p>A client component — call it after the user chooses to opt in.</p>
      <Code fname="app/enable-notifications.tsx" copyText={BUTTON}>
        {BUTTON}
      </Code>

      <h2>4. Send from the server</h2>
      <p>A server action keeps the call server-side. Trigger it from anywhere on your backend.</p>
      <Code fname="app/actions.ts" copyText={ACTION}>
        {ACTION}
      </Code>
      <Code badge="server" copyText={CALL}>
        {CALL}
      </Code>

      <div className="callout">
        That’s the whole loop. If nothing appears: confirm notifications are allowed for your site and
        that <span className="icode">/notify-sw.js</span> loads at your domain root.
      </div>

      <div className="doc-foot">
        <Link to="/docs/quickstart" className="ilink">
          ← Quickstart
        </Link>
        <Link to="/docs/tanstack-start" className="ilink">
          TanStack Start →
        </Link>
      </div>
    </>
  );
}
